import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  CampaignService,
  CampaignStepInterface,
  CampaignStepInterfaceInterface,
  CampaignStepType,
  ConfigService,
  EmailTemplateService,
  FieldMaskInterface,
  Focuses,
  GetterCampaignData,
  LegacyAppengineService,
  ListCampaignsResponse,
  ListRecommendedCampaignsRequestInterface,
  RecipientCampaignService,
  Sender,
  SenderInterface,
  SenderType,
  Statuses,
  StepDataResponse,
  UpdateConfigResponse,
  GetConfigResponse,
  RecipientCampaignStatus,
  PagedRequestOptionsInterface,
} from '@vendasta/campaigns';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '../../api-service/api.service';
import { AutomationConstants, AutomationIds, CampaignAutomations } from '../campaign';
import { CampaignPreviewInterface, CampaignStatus } from './interface';
import { AccountGroup } from '@galaxy/account-group';

export const SNAPSHOT_STEP_NAME = 'Snapshot Creation';
export const DEFAULT_SEND_DELAY = 5 * 60; // 5 minutes

export const MAILING_INFO_NOT_CONFIGURED = 'mailing-info-not-configured';
const exampleEmail = 'example@example.com';

export const GENERIC_ACCOUNT_GROUP = {
  accountGroupId: 'AG-123',
  napData: {
    companyName: 'Generic Name',
  },
} as AccountGroup;

@Injectable({ providedIn: 'root' })
export class CampaignsService {
  cs: typeof CampaignStatus = CampaignStatus;

  constructor(
    private campaignAPIService: CampaignService,
    private templateService: EmailTemplateService,
    private legacyCampaignService: LegacyAppengineService,
    private configService: ConfigService,
    private rcService: RecipientCampaignService,
    private apiService: ApiService,
    private readonly http: HttpClient,
  ) {}

  createCampaign(sender: SenderInterface, name: string): Observable<string> {
    return this.campaignAPIService.create(sender as Sender, name);
  }

  deleteCampaign(campaignId: string): Observable<null> {
    return this.campaignAPIService.delete(campaignId);
  }

  getCampaign(campaignID: string): Observable<GetterCampaignData> {
    return this.campaignAPIService.get(campaignID);
  }

  listCampaigns(
    partnerID: string,
    focuses?: Focuses[],
    statuses?: Statuses[],
    name?: string,
    pagingOptions?: PagedRequestOptionsInterface,
  ): Observable<GetterCampaignData[]> {
    return this.campaignAPIService.list(partnerID, focuses, statuses, name, pagingOptions);
  }

  listRecommendedCampaigns(data: ListRecommendedCampaignsRequestInterface): Observable<ListCampaignsResponse> {
    return this.campaignAPIService.listRecommendedCampaigns(data);
  }

  updateStatus(sender: SenderInterface, campaignID: string, status: Statuses): Observable<void> {
    return this.campaignAPIService.updateStatus(sender, campaignID, status);
  }

  pauseCampaign(campaignId: string): Observable<void> {
    return this.campaignAPIService.pauseCampaign(campaignId);
  }

  unpauseCampaign(campaignId: string): Observable<void> {
    return this.campaignAPIService.unpauseCampaign(campaignId);
  }

  updateCampaignLocale(sender: SenderInterface, campaignID: string, locale: string): Observable<void> {
    return this.campaignAPIService.updateCampaignLocale(sender, campaignID, locale);
  }

  updateCampaignSchedule(
    sender: SenderInterface,
    campaignID: string,
    campaignSchedule: CampaignStepInterfaceInterface[],
  ): Observable<void> {
    return this.campaignAPIService.updateCampaignSchedule(sender, campaignID, campaignSchedule);
  }

  updateStep(
    sender: SenderInterface,
    campaignId: string,
    campaignStepId: string,
    contentId: string,
    name: string,
    secondsAfterLastStep: number,
    fm: FieldMaskInterface,
  ): Observable<StepDataResponse> {
    return this.campaignAPIService.updateStep(
      sender,
      campaignId,
      campaignStepId,
      contentId,
      name,
      secondsAfterLastStep,
      fm,
    );
  }

  addSnapshotStep(sender: SenderInterface, campaignId: string): Observable<StepDataResponse> {
    return this.campaignAPIService.addStep(
      sender,
      campaignId,
      CampaignStepType.CAMPAIGN_STEP_TYPE_SNAPSHOT_CREATION,
      '',
      SNAPSHOT_STEP_NAME,
    );
  }

  isSnapshotStep(step: CampaignStepInterfaceInterface): boolean {
    return step.stepType === CampaignStepType.CAMPAIGN_STEP_TYPE_SNAPSHOT_CREATION;
  }

  hasSnapshotStep(campaignId: string): Observable<boolean> {
    return this.campaignAPIService.get(campaignId).pipe(
      map((campaign) => {
        return campaign.campaignSchedule.some((step) => this.isSnapshotStep(step));
      }),
    );
  }

  addStep(
    sender: SenderInterface,
    campaignId: string,
    stepType: CampaignStepType,
    contentId: string,
    name: string,
  ): Observable<StepDataResponse> {
    return this.campaignAPIService.addStep(sender, campaignId, stepType, contentId, name);
  }

  updateName(sender: SenderInterface, campaignID: string, name: string): Observable<void> {
    return this.campaignAPIService.updateName(sender, campaignID, name);
  }

  updateRecipientCampaignStatus(recipientCampaignId: string, status: RecipientCampaignStatus): Observable<null> {
    return this.legacyCampaignService.updateRecipientCampaignStatus(recipientCampaignId, status);
  }

  getConfig(campaignId: string): Observable<GetConfigResponse> {
    return this.configService.getConfig(campaignId);
  }

  updateCampaignConfig(
    sender: SenderInterface,
    campaignId: string,
    timezone: string,
    validDays: string[],
    rateLimitEnabled: boolean,
    rateLimit: number,
  ): Observable<UpdateConfigResponse> {
    return this.configService.updateConfig(campaignId, timezone, [], rateLimitEnabled, rateLimit, sender, validDays);
  }

  convertFocusToProto(focus: string): Focuses {
    switch (focus) {
      case 'acquire':
        return Focuses.FOCUSES_ACQUIRE;
      case 'adopt':
        return Focuses.FOCUSES_ADOPT;
      case 'upsell':
        return Focuses.FOCUSES_UPSELL;
      case 'none':
        return Focuses.FOCUSES_NONE;
      default:
        return Focuses.FOCUSES_ACQUIRE;
    }
  }

  statusFromProto(status: Statuses): string {
    switch (status) {
      case Statuses.STATUSES_DRAFT:
        return this.cs.DRAFT;
      case Statuses.STATUSES_PUBLISHED:
        return this.cs.PUBLISHED;
      case Statuses.STATUSES_ACTIVE:
        return this.cs.ACTIVE;
      case Statuses.STATUSES_ARCHIVED:
        return this.cs.ARCHIVED;
      default:
        return '';
    }
  }

  focusFromProto(focus: Focuses): string {
    switch (focus) {
      case Focuses.FOCUSES_ACQUIRE:
        return 'acquire';
      case Focuses.FOCUSES_UPSELL:
        return 'upsell';
      case Focuses.FOCUSES_ADOPT:
        return 'adopt';
      case Focuses.FOCUSES_NONE:
        return 'none';
      default:
        return '';
    }
  }

  //Legacy Ajax endpoints

  private _stepIdForPreview = '';
  private _prefetchTemplatePreviews$$: BehaviorSubject<{ [templateId: string]: CampaignPreviewInterface }> =
    new BehaviorSubject({});

  getPrefetchedPreviewByStepId(stepId: string): CampaignPreviewInterface {
    return this._prefetchTemplatePreviews$$.value[stepId];
  }

  loadPreviews(
    partnerID: string,
    marketID: string,
    accountGroupID: string,
    locale: string,
    steps: CampaignStepInterface[],
  ) {
    const previews$ = steps.map((step) => {
      return this.getHTMLContentOfEmail(partnerID, marketID, step.templateId, accountGroupID, locale).pipe(
        map((preview) => ({ ...preview, stepId: step.campaignStepId })),
      );
    });
    combineLatest(previews$).subscribe((previews) => {
      const newPreviews = {};
      previews.forEach((preview) => (newPreviews[preview.stepId] = preview));
      this._prefetchTemplatePreviews$$.next(newPreviews);
    });
  }

  get stepIDForPreview(): string {
    return this._stepIdForPreview;
  }

  set stepIDForPreview(stepId: string) {
    this._stepIdForPreview = stepId;
  }

  getHTMLContentOfEmail(
    partnerId: string,
    marketId: string,
    templateId: string,
    accountGroupId = '',
    locale?: string,
  ): Observable<CampaignPreviewInterface> {
    if (templateId === 'snapshot-creation') {
      return of({
        html: '',
        name: '',
        subject: '',
      });
    }
    return this.templateService
      .previewEmailTemplate(templateId, accountGroupId, partnerId, marketId, locale, '', {
        type: SenderType.SENDER_TYPE_PARTNER,
        id: partnerId,
      })
      .pipe(
        map((preview) => ({
          html: preview.htmlBody,
          name: preview.name,
          subject: preview.subject,
        })),
      );
  }
  getCampaignAutiomations(campaignId: string, partnerId: string, marketId: string): Observable<CampaignAutomations[]> {
    const url = `/_ajax/v1/campaign/automation/lookup?campaignId=${campaignId}&partnerId=${partnerId}&marketId=${marketId}&actionIds=${AutomationConstants.START_CAMPAIGN}&actionIds=${AutomationConstants.STOP_CAMPAIGN}`;
    const automations: CampaignAutomations[] = [];
    return this.apiService.get(url).pipe(
      map((res) => {
        res.forEach((a) => {
          automations.push({
            automationID: a.automation_id,
            productId: a.product_id,
            triggerId: a.trigger_id,
            actionID: a.action_id,
          });
        });
        return automations;
      }),
    );
  }

  updateAutomationsConfig(
    startAutomationProductId: string,
    startProductId: string,
    stopAutomationProductId: string,
    stopProductId: string,
    marketId: string,
    campaignId: string,
  ): Observable<AutomationIds> {
    const url = `/_ajax/v1/campaign/automation/update`;
    const formData = new FormData();
    formData.set('startAutomationProductId', startAutomationProductId);
    formData.set('startAutomationId', startProductId);
    formData.set('stopAutomationProductId', stopAutomationProductId);
    formData.set('stopAutomationId', stopProductId);
    formData.set('marketId', marketId);
    formData.set('campaignId', campaignId);
    return this.http.post<{ data: AutomationIds; withCredentials: true }>(url, formData).pipe(map((r) => r.data));
  }

  legacyStepTypeToProto(stepType: CampaignStepType): string {
    switch (stepType) {
      case CampaignStepType.CAMPAIGN_STEP_TYPE_EMAIL:
        return 'email';
      case CampaignStepType.CAMPAIGN_STEP_TYPE_SNAPSHOT_CREATION:
        return 'snapshot-creation';
      case CampaignStepType.CAMPAIGN_STEP_TYPE_SMS:
        return 'sms';
      default:
        return '';
    }
  }

  validateForRecipient(
    campaignSchedule: CampaignStepInterface[],
    partnerId: string,
    accountGroupId: string,
    marketId: string,
  ): Observable<any> {
    const formData = new FormData();
    const scheduleJSON: {
      campaign_step_id: string;
      step_type: string;
      template_id: string;
      seconds_after_last_email: number;
      name: string;
    }[] = [];

    campaignSchedule.forEach((step) => {
      scheduleJSON.push({
        campaign_step_id: step.campaignStepId,
        step_type: this.legacyStepTypeToProto(step.stepType),
        template_id: step.templateId,
        seconds_after_last_email: step.secondsAfterLastEmail ? step.secondsAfterLastEmail : 0,
        name: step.name,
      });
    });

    formData.set('campaignScheduleJson', JSON.stringify(scheduleJSON));
    formData.set('partnerId', partnerId);
    formData.set('accountGroupId', accountGroupId);
    formData.set('email', exampleEmail);
    formData.set('marketId', marketId);
    return this.http.post<any>('/_ajax/v1/campaign/validateForRecipient', formData).pipe(
      map((resp) => {
        return resp.data;
      }),
    );
  }

  sendTestEmail(
    partnerId: string,
    templateId: string,
    email: string,
    marketId = '',
    accountGroupId = '',
    locale?: string,
    renderFakeData?: boolean,
  ): Observable<void> {
    const formData = new FormData();
    formData.set('partnerId', partnerId);
    formData.set('templateId', templateId);
    formData.set('recipientEmail', email);
    formData.set('marketId', marketId);
    formData.set('accountGroupId', accountGroupId);
    formData.set('renderFakeDataFlag', renderFakeData ? 'true' : 'false');
    if (locale) {
      formData.set('locale', locale);
    }
    return this.http.post<void>('/_ajax/v1/campaign/template/preview/send/', formData);
  }

  updateCampaignFocus(campaignId: string, focus: string): Observable<null> {
    //TODO: deprecate this in favour of tags [MAEL-1024]
    const formData = new FormData();
    formData.set('focus', focus);
    formData.set('campaignId', campaignId);
    return this.http
      .post<{ data: null }>('/_ajax/v1/campaign/focus/update', formData, {
        withCredentials: true,
      })
      .pipe(map((resp) => resp.data));
  }

  updateCampaignEmailCategory(campaignId: string, emailCategoryId: string): Observable<null> {
    const formData = new FormData();
    formData.set('emailCategoryId', emailCategoryId);
    formData.set('campaignId', campaignId);
    return this.http
      .post<{ data: null }>('/_ajax/v1/campaign/email-category/update', formData, {
        withCredentials: true,
      })
      .pipe(map((resp) => resp.data));
  }
}
